<template>
	<section
		ref="navSidebarGroup"
		:class="classes"
		@click="onClick"
	>
		<NavSidebarTooltip
			class-name="nav-sidebar-group__tooltip"
			v-if="$slots.tooltip && $refs.navSidebarGroup"
			v-bind="tooltipBindings"
		>
			<slot name="tooltip"></slot>
		</NavSidebarTooltip>
		<div :class="headingClasses">
			<span class="nav-sidebar-group__icon" v-if="$slots.icon">
				<slot name="icon"></slot>
			</span>
			<h1 :class="textClasses">
				<slot name="text" v-if="$slots.text"></slot>
			</h1>
		</div>
		<main class="nav-sidebar-group__items">
			<slot name="items"></slot>
		</main>
	</section>
</template>

<script>
import throttle from "lodash/throttle";
import Enum from "@/constants/Enum";
import NavSidebarTooltip from "./NavSidebarTooltip";

export const Themes = Object.create( Enum );
Themes.PRIMARY = "PRIMARY";
Themes.SPECIAL = "SPECIAL";
Themes.PAYMENT = "PAYMENT";

export const Scales = Object.create( Enum );
Scales.NORMAL = "NORMAL";
Scales.SMALL = "SMALL";

export default {
	name: "NavSidebarGroup",
	components: {
		NavSidebarTooltip,
	},
	props: {
		className: {
			type: String,
			default: "",
		},
		isSelected: {
			type: Boolean,
			default: false,
		},
		theme: {
			type: String,
			default: "primary",
		},
		scale: {
			type: String,
			default: "normal",
		},
	},
	data() {
		return {
			resizeHandler: null,
			windowWidth: 0,
		};
	},
	computed: {
		classes() {
			return {
				"nav-sidebar-group": true,
				"nav-sidebar-group--selected": this.isSelected,
				[ this.className ]: !!this.className,
			};
		},
		headingClasses() {
			const baseClass = "nav-sidebar-group__heading";
			if ( this.theme === Themes.SPECIAL ) {
				return `${ baseClass } teaming-theme--special`;
			}
			if ( this.theme === Themes.PAYMENT ) {
				return `${ baseClass } teaming-theme--payment`;
			}
			return `${ baseClass } teaming-theme--primary`;
		},
		textClasses() {
			const baseClass = "nav-sidebar-group__text";
			if ( this.scale === Scales.SMALL ) {
				return `${ baseClass } teaming-scale--small`;
			}
			return `${ baseClass } teaming-scale--normal`;
		},
		tooltipBindings() {
			const bindings = {
				// force these bindings to recompute
				// whenever the window width changes
				_: this.windowWidth,
				groupTop: 0,
				groupBottom: 0,
				groupLeft: 0,
				groupRight: 0,
			};

			if ( !this.$refs.navSidebarGroup ) {
				return bindings;
			}

			const {
				top: groupTop,
				bottom: groupBottom,
				right: groupRight,
			} = this.$refs.navSidebarGroup.getBoundingClientRect();

			return {
				groupTop,
				groupBottom,
				groupRight,
			};
		},
	},
	methods: {
		onClick( e ) {
			e.stopPropagation();
			this.$emit( "click" );
		},
	},
	mounted() {
		this.resizeHandler = throttle( () => {
			this.windowWidth = window.innerWidth;
			// eslint-disable-next-line no-magic-numbers
		}, 200 );
		window.addEventListener( "resize", this.resizeHandler );
	},
	beforeUnmount() {
		if ( this.resizeHandler ) {
			window.removeEventListener( "resize", this.resizeHandler );
			this.resizeHandler = null;
		}
	},
};
</script>

<style lang="scss" scoped>
.nav-sidebar-group {
	&__heading {
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		align-items: center;

		border-radius: 8px;
		padding: 8px;

		cursor: pointer;
		position: relative;
	}

	&__heading.teaming-theme--primary {
		color: $gray-500;
		background-color: $mono-000;
	}

	&__heading.teaming-theme--special {
		color: $violet-600;
		background-color: $violet-100;
	}

	&__heading.teaming-theme--payment {
		color: $green-600;
		background-color: $green-100;
	}

	&__heading.teaming-theme--secondary {}

	&__icon {
		display: inline-flex;
		justify-content: center;
		flex: 0 0 24px;
		margin: 0 16px 0 0;
		padding: 0;

		position: relative;
	}

	&__text {
		margin: 0;
		padding: 0;
		display: inline-flex;

		font-style: normal;
		font-size: 16.8px;
		line-height: 24px;

		@include for-size('sm') {
			display: none;
		}

		@include for-size('lg') {
			display: inline-flex;
		}
	}

	&__text.teaming-scale--normal {
		font-style: normal;
		font-size: 16.8px;
		line-height: 24px;
	}

	&__text.teaming-scale--small {
		font-style: normal;
		font-size: 14px;
		line-height: 20px;
	}

	&--selected {
		.nav-sidebar-group__heading.teaming-theme--primary {
			color: $mono-000;
			background-color: $blue-500;
		}

		.nav-sidebar-group__heading.teaming-theme--special {
			color: $violet-600;
			background-color: $violet-100;
		}
	}

	&:not(&--selected):hover {
		.nav-sidebar-group__heading.teaming-theme--primary {
			color: $blue-600;
			background-color: $blue-100;
		}

		.nav-sidebar-group__heading.teaming-theme--special {
			color: $violet-100;
			background-color: $violet-600;
		}

		.nav-sidebar-group__heading.teaming-theme--payment {
			color: $white;
			background-color: $green-500;
		}
	}

	&__items {
		display: none;

		@include for-size('lg') {
			display: block;
		}
	}
}

.nav-sidebar-group__tooltip {
	visibility: hidden;
}

.nav-sidebar-group:hover {
	.nav-sidebar-group__tooltip {
		@include for-size('md') {
			visibility: visible;
		}

		@include for-size('lg') {
			visibility: hidden;
		}
	}
}
</style>



// WEBPACK FOOTER //
// src/components/Sidebar/NavSidebarGroup.vue